import { FunctionComponent, useCallback } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import styled, { css, useTheme } from 'styled-components'

import config from '@config'
import { desktopCSS } from '@measures/responsive'
import translate from '@i18n'
import SearchPageContext from '@contexts/searchPageContext'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useURLParam from '@hooks/useURLParam'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import JSONRenderer from '@components/JSONRenderer'
import SearchComponents from '@components/Search'
import EnhancedSearchResultsHeader from '@components/Search/EnhancedSearchResultsHeader'
import SvgIcon from '@components/SvgIcon'

import { EnhancedSearchResponseAPIProps, SearchResponseAPIProps } from './types'
import {
  enrichListItemTeasers,
  getResultNumberRange,
  getResults,
} from './utils'

const {
  Container,
  SearchInput,
  ResultsContainer,
  ResultsHeader,
  SearchMessage,
} = SearchComponents

const {
  search: { sectionUrl },
} = config

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  ${({
    theme: {
      spacing: { spacing56 },
    },
  }) => css`
    width: 100%;
    margin-top: ${spacing56};
    ${desktopCSS(css`
      margin-top: 68px;
    `)}
  `}
`

const rangeZeroResult = getResultNumberRange(0)

const EnhancedSearch: FunctionComponent = () => {
  const router = useRouter()
  const q = useURLParam('q')
  const searchTerm = !!q && typeof q === 'string' ? q : ''

  const theme = useTheme()

  const onNoResultTrack = useCallback<
    TrackingFnType<{ searchTerm: string | undefined }>
  >(({ extraData }) => {
    const { searchTerm } = extraData
    return {
      event: 'search_zero_result',
      eventCategory: 'search',
      eventLabel: searchTerm,
      eventAction: 'zero_result',
      search_term: searchTerm?.toLowerCase(),
      q_search_result_range: rangeZeroResult,
    }
  }, [])
  const trackingOnSearch = useCallback<TrackingFnType<{ searchTerm: string }>>(
    ({ extraData }) => {
      const { searchTerm } = extraData
      return {
        event: 'search',
        eventCategory: 'search',
        eventLabel: searchTerm.toLowerCase(),
        eventAction: 'submitted',
        search_term: searchTerm.toLowerCase(),
      }
    },
    []
  )

  const handleSearchTrack = useTracking(trackingOnSearch)

  const noResultTrack = useTracking(onNoResultTrack)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery<
    SearchResponseAPIProps,
    unknown,
    EnhancedSearchResponseAPIProps,
    ['searchData', string],
    number
  >({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['searchData', searchTerm],
    queryFn: ({ pageParam = 0 }) =>
      getResults(searchTerm, pageParam, noResultTrack),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.total_pages - 1
        ? lastPage.page + 1
        : undefined
    },
    select: (data): EnhancedSearchResponseAPIProps => {
      const transformedData = data.pages.flatMap((page) =>
        enrichListItemTeasers(page.content)
      )
      return {
        ...data,
        transformed_results: transformedData,
        total_elements: data.pages?.[0]?.total_elements,
      }
    },
    enabled: !!searchTerm,
    gcTime: 0,
  })
  const resultsAvailable =
    !isLoading && !isError && data && data.transformed_results?.length > 0
  const transformedResults = data?.transformed_results || []
  const totalElements: number = data?.total_elements || 0

  const updateSearchTerm = (value: string) => {
    const trimmedSearchTerm = value.trim()
    if (trimmedSearchTerm !== '' && trimmedSearchTerm !== searchTerm) {
      handleSearchTrack({ searchTerm: trimmedSearchTerm })
      router.push(
        {
          pathname: sectionUrl,
          query: { q: trimmedSearchTerm },
        },
        undefined,
        { shallow: true }
      )
    }
  }

  return (
    <SearchPageContext.Provider value={true}>
      <Container>
        <ResultsHeader>
          <SearchInput initialValue={searchTerm} onSubmit={updateSearchTerm} />
          <EnhancedSearchResultsHeader
            searchTerm={searchTerm}
            isLoading={isLoading}
            totalElements={totalElements}
          />
        </ResultsHeader>
        <ResultsContainer>
          {((!resultsAvailable && !isLoading) || isError) && (
            <SearchMessage>
              {translate(
                isError ? 'search.errorMessage' : 'search.noResultsExplanation'
              )}
            </SearchMessage>
          )}
          <JSONRenderer>{transformedResults}</JSONRenderer>
        </ResultsContainer>
        {hasNextPage && (
          <StyledPrimaryCTAButton
            type="button"
            size="large"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}>
            {translate('enhancedSearch.loadButton')}
            <SvgIcon
              iconName="chevron-down"
              size={24}
              color={theme.colors.iconDefault}
            />
          </StyledPrimaryCTAButton>
        )}
      </Container>
    </SearchPageContext.Provider>
  )
}

export default EnhancedSearch
